// Proove Report Upload — staff-only dialog, two intake paths:
//
//   Path A (orderContext prop supplied) — reached from the "Awaiting
//     Proove Report" queue tab / OrderDetailDialog in LabOrdersView.js.
//     Identity fields (patient name/DOB/address, practice, provider NPI/
//     name) come straight from the order's own already-loaded detail —
//     shown READ-ONLY (greyed out) so staff can visually confirm them
//     without being able to retype/typo them; the backend re-derives
//     these same fields server-side from the order anyway (never trusts
//     the client even if it did send them), this is purely a display
//     safeguard against submitting the wrong order's upload by mistake.
//
//   Path B (no orderContext, OR staff clicks "Can't find this order?
//     Enter details manually" from Path A) — the manual/paper-
//     requisition fallback. Same field shape as Report_Generation.js's
//     own native report-upload identity form (upload_report_to_gcs).
//
// 2026-10-21 revamp (explicit user request: "the ability to send
// individually to clinician and patient also like with a toggle each" +
// "the forms are basic, no validation, no comfortable UI") — two real
// changes from the previous version:
//
//   1. Independent "Send to Patient" / "Send to Clinician" toggles.
//      Both default ON (same behavior as before this revamp). Exactly
//      one ON routes through the existing, unchanged single-PDF
//      endpoint (POST /api/proove-reports/upload, report_kind set to
//      whichever flavor is on) — the same endpoint retryFailedFlavor
//      already used, just now reachable directly, not only as a retry.
//      Both ON keeps using the combined endpoint, unchanged. At least
//      one toggle must stay on — turning the last one off re-enables
//      the other automatically rather than leaving nothing selected.
//
//   2. Real inline validation, not just a silently-disabled submit
//      button: NPI format (10 digits, same /^\d{10}$/ rule
//      Report_Generation.js already enforces — required only when the
//      Clinician toggle is on, since that's the only flavor that field
//      actually matters for), ZIP format (reuses isValidZip, the same
//      shared helper Report_Generation.js/Register.js already use),
//      and every required text field shows a red border + specific
//      helper text once the field has been touched or a submit was
//      attempted, never just a greyed-out button with no explanation.
//
//   2026-10-22 (Piece 5, one-identity-one-email invariant, confirmed
//   decision reversal) — Patient Email is now ALWAYS required and ALWAYS
//   shown, regardless of which Send-To toggle(s) are on (previously only
//   required/shown when the Patient toggle was on). "Clinician kind"
//   means which COPY is being sent, not "no patient involved" — the
//   patient is always the subject either way, and the backend's identity
//   resolution/email-claim check runs underneath regardless of which
//   copy is being archived. Without this, a clinician-only send would
//   resolve the identity but never check it against an email, silently
//   missing a real identity/email conflict.
//
// A combined-upload response can be a PARTIAL success (one flavor
// archived, the other failed after it) — see submit()'s own handling
// below; this is never silently shown as a full "Report uploaded."
//
// This is a STAFF-ONLY feature — both upload endpoints require a staff
// bearer token (get_current_user, same as upload_report_to_gcs), not an
// admin token, so this dialog is only ever mounted from the staff
// surface of LabOrdersView (isAdmin=false). api.js's own axios instance
// already attaches the staff "token" from localStorage automatically.
import React, { useEffect, useState } from "react";
import api from "../api";
import { extractErrorDetail } from "./validators";
import {
  Box, Typography, TextField, Button, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Select, MenuItem, FormControl, InputLabel, Switch, Paper, Chip, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { EMPTY_ADDRESS, AddressFields, isValidZip } from "./addressShared";

const BRAND_NAVY = "#00357a";
const BRAND_NAVY_DARK = "#001f4d";
const BRAND_GREEN = "#1a7a4c";
const BRAND_GREEN_DARK = "#0f5536";

// Same 3 ids/display names as LabOrdersView.js's own TEST_ID_DISPLAY_
// NAMES / the backend's _PROOVE_TEST_ID_DISPLAY_NAMES — recreated here
// rather than imported, same "separate frontend package, recreate don't
// cross-import" convention LabOrdersView.js's own header comment uses.
const PROOVE_TEST_OPTIONS = [
  { value: "proove_pgx", label: "Proove Drug Metabolism™ Profile (PGx)" },
  { value: "proove_opioid_risk", label: "Proove Opioid Risk™ Profile" },
  { value: "proove_pain_perception", label: "Proove Pain Perception™ Profile" },
];

const NPI_RE = /^\d{10}$/;

// User-directed, 2026-10-22 — must match the backend's
// PROOVE_REPORT_JSON_ENABLED (not auto-synced). When true, an optional
// JSON file field appears beside each PDF field — staff can supply the
// already-correct ai_summary/stats_json directly (from Proove's own
// generation-time data) instead of relying on this app's own OCR/Vertex
// extraction. PDF stays always required either way; omitting the JSON
// for a flavor just leaves that flavor's summary/stats blank (no
// automatic OCR fallback) — see the backend route's own comment.
const PROOVE_REPORT_JSON_ENABLED = process.env.REACT_APP_PROOVE_REPORT_JSON_ENABLED === "true";

const readOnlyFieldSx = {
  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#666" },
  "& .MuiOutlinedInput-root.Mui-disabled": { bgcolor: "#F4F4F5" },
};

function SectionHeader({ children, required }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, mt: 2.5 }}>
      <Box sx={{ width: 3, height: 18, backgroundColor: BRAND_NAVY, borderRadius: 1 }} />
      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: BRAND_NAVY }}>{children}</Typography>
      {required && (
        <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>Required</Typography>
      )}
    </Box>
  );
}

// One "recipient" toggle card — pairs the Send-to-X switch with its own
// file picker so the relationship between "this is on" and "this file is
// needed" is visually obvious, not two unrelated controls on opposite
// sides of the form the way the old version had them.
function RecipientCard({ active, onToggle, color, colorDark, icon, title, subtitle, file, onFile, fileRequired, fileError, jsonEnabled, jsonFile, onJsonFile }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1, minWidth: 240, borderRadius: 2.5, overflow: "hidden",
        borderColor: active ? color : "rgba(0,0,0,0.12)",
        borderWidth: active ? 2 : 1,
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        boxShadow: active ? `0 0 0 3px ${color}1a` : "none",
      }}
    >
      <Box sx={{
        px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.25,
        bgcolor: active ? color : "#f4f4f5",
        color: active ? "#fff" : "#9ca3af",
        transition: "background-color 120ms ease",
      }}>
        {icon}
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
          <Typography sx={{ fontSize: 11, opacity: 0.85 }}>{subtitle}</Typography>
        </Box>
        <Switch
          checked={active}
          onChange={onToggle}
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: "#fff" },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "rgba(255,255,255,0.5)" },
            "& .MuiSwitch-track": { bgcolor: active ? "rgba(255,255,255,0.35)" : undefined },
          }}
        />
      </Box>
      <Box sx={{ p: 2, opacity: active ? 1 : 0.45, pointerEvents: active ? "auto" : "none" }}>
        <Button
          component="label" fullWidth variant={file ? "outlined" : "contained"}
          startIcon={file ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : <CloudUploadIcon sx={{ fontSize: 16 }} />}
          disabled={!active}
          sx={{
            textTransform: "none", justifyContent: "flex-start", fontSize: 13,
            ...(file
              ? { borderColor: color, color: colorDark, "&:hover": { borderColor: colorDark, bgcolor: `${color}0d` } }
              : { bgcolor: color, "&:hover": { bgcolor: colorDark } }),
          }}
        >
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {file ? file.name : "Choose PDF file"}
          </Box>
          <input type="file" hidden accept="application/pdf" onChange={(e) => onFile(e.target.files?.[0] || null)} />
        </Button>
        {file && (
          <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.5 }}>
            {(file.size / 1024).toFixed(0)} KB
          </Typography>
        )}
        {fileRequired && !file && fileError && (
          <Typography sx={{ fontSize: 11.5, color: "#dc2626", mt: 0.5 }}>A PDF is required for this recipient.</Typography>
        )}
        {jsonEnabled && (
          <>
            <Button
              component="label" fullWidth variant={jsonFile ? "outlined" : "text"}
              startIcon={jsonFile ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <CloudUploadIcon sx={{ fontSize: 14 }} />}
              disabled={!active}
              sx={{ textTransform: "none", justifyContent: "flex-start", fontSize: 12, mt: 1, color: jsonFile ? colorDark : "#64748b" }}
            >
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {jsonFile ? jsonFile.name : "Choose JSON file (optional)"}
              </Box>
              <input type="file" hidden accept="application/json" onChange={(e) => onJsonFile(e.target.files?.[0] || null)} />
            </Button>
            <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 0.25 }}>
              {jsonFile
                ? "Supplied directly — this skips OCR extraction for this recipient."
                : "Optional — if omitted, this recipient's summary/stats stay blank (no automatic OCR)."}
            </Typography>
          </>
        )}
      </Box>
    </Paper>
  );
}

// orderContext (Path A), when supplied: {
//   order_id, proove_test_ids (array, filtered to just the Proove ones
//   present on this order), patient: {first_name,last_name,dob,
//   address_line1,address_unit,address_city,address_state,address_zip},
//   practice_name, npi, provider_name,
// }
export default function UploadProoveReport({ open, onClose, orderContext, onUploaded }) {
  const [manualMode, setManualMode] = useState(!orderContext);
  const [prooveTestId, setProoveTestId] = useState("");
  const [sendToPatient, setSendToPatient] = useState(true);
  const [sendToClinician, setSendToClinician] = useState(true);
  const [patientFile, setPatientFile] = useState(null);
  const [clinicianFile, setClinicianFile] = useState(null);
  const [patientJsonFile, setPatientJsonFile] = useState(null);
  const [clinicianJsonFile, setClinicianJsonFile] = useState(null);

  // Manual (Path B) fields — also used as the EDITABLE copy when manual
  // mode is toggled on from a Path A context.
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [address, setAddress] = useState({ ...EMPTY_ADDRESS });
  const [practiceName, setPracticeName] = useState("");
  const [providerNpi, setProviderNpi] = useState("");
  const [providerName, setProviderName] = useState("");
  const [providerPhone, setProviderPhone] = useState("");
  // Phase 0, task 9 (backend) — required exactly when there's no matched
  // order_id (manual/no-order intake): the backend 422s without it, since
  // without a real specimen/accession number a manual upload has no dedup
  // key at all (see main.py's own "KNOWN RESIDUAL GAP" comment on
  // _archive_proove_report).
  const [manualSpecimenId, setManualSpecimenId] = useState("");
  // 2026-10-21 fix (user-directed — "patient email is not optional"),
  // widened 2026-10-22 (Piece 5) — always required now, regardless of
  // which Send-To toggle is on; see patientEmailMissing below. Serves
  // two purposes: the report-ready notification (patient-flavor sends
  // only), and the one-identity-one-email identity-claim check the
  // backend now runs on EVERY upload (both flavors).
  const [patientEmail, setPatientEmail] = useState("");
  // 2026-10-20 addition — optional, manual (no-order) intake only.
  const [collectionDate, setCollectionDate] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successLabel, setSuccessLabel] = useState("");
  // Set on a partial-success response (one flavor archived, the other
  // failed after it) — never collapsed into the plain `success` state,
  // since that would silently claim a full success that didn't happen.
  const [partialResult, setPartialResult] = useState(null);
  // Only show field-level validation errors after the user has actually
  // tried something — never greet a freshly-opened dialog with a wall of
  // red borders before they've typed anything.
  const [touched, setTouched] = useState(false);

  // Reset/prefill every time the dialog is (re)opened for a (possibly
  // different) order — never carry over a previous upload's state.
  useEffect(() => {
    if (!open) return;
    setManualMode(!orderContext);
    setSendToPatient(true);
    setSendToClinician(true);
    setPatientFile(null);
    setClinicianFile(null);
    setPatientJsonFile(null);
    setClinicianJsonFile(null);
    setSubmitting(false); setError(""); setSuccess(false); setSuccessLabel(""); setPartialResult(null);
    setTouched(false);
    setManualSpecimenId("");
    setPatientEmail("");
    setCollectionDate("");
    if (orderContext) {
      setProoveTestId((orderContext.proove_test_ids || [])[0] || "");
      setFirstName(orderContext.patient?.first_name || "");
      setLastName(orderContext.patient?.last_name || "");
      setDob(orderContext.patient?.dob || "");
      setAddress({
        line1: orderContext.patient?.address_line1 || "",
        unit: orderContext.patient?.address_unit || "",
        city: orderContext.patient?.address_city || "",
        state: orderContext.patient?.address_state || "",
        zip: orderContext.patient?.address_zip || "",
      });
      setPracticeName(orderContext.practice_name || "");
      setProviderNpi(orderContext.npi || "");
      setProviderName(orderContext.provider_name || "");
      // 2026-10-23 (Piece 7, ui-ux-checker finding) — unlike its three
      // siblings directly above, phone has no order-derived display value
      // (it's resolved server-side from the clinician's own account, never
      // shown here) — but it must still be explicitly cleared on every
      // dialog open, not left stale from a PRIOR manual-entry session,
      // since this modal's state isn't guaranteed to be destroyed between
      // opens (`open` is a prop, not a mount/unmount).
      setProviderPhone("");
      // 2026-10-22 (Piece 5, ui-ux-checker finding) — prefill from the
      // order's own patient record, same as every other identity field
      // above, now that this field is mandatory on every upload. Only a
      // STARTING value — staff can still edit it, and the backend's own
      // identity-claim check runs on whatever is actually submitted.
      setPatientEmail(orderContext.patient?.email || "");
    } else {
      setProoveTestId("");
      setFirstName(""); setLastName(""); setDob("");
      setAddress({ ...EMPTY_ADDRESS });
      setPracticeName(""); setProviderNpi(""); setProviderName(""); setProviderPhone("");
    }
  }, [open, orderContext]);

  const usingOrder = !!orderContext && !manualMode;

  // Exactly one recipient toggled off is fine (single-flavor send); BOTH
  // off is not a valid state — flipping the last one off snaps the other
  // back on instead of leaving nothing selected with no clear way to
  // recover other than guessing.
  const toggleRecipient = (which) => {
    if (which === "patient") {
      if (sendToPatient && !sendToClinician) { setSendToClinician(true); return; }
      setSendToPatient((v) => !v);
    } else {
      if (sendToClinician && !sendToPatient) { setSendToPatient(true); return; }
      setSendToClinician((v) => !v);
    }
  };

  const bothOn = sendToPatient && sendToClinician;

  // ── Field-level validation state (drives inline errors, not just the
  // submit button) ─────────────────────────────────────────────────────
  const npiTouchedInvalid = sendToClinician && providerNpi.trim() && !NPI_RE.test(providerNpi.trim());
  // 2026-10-23 (clinician invite-only build, Piece 7) — provider_phone is
  // only ever a typed field here on Path B (manual/no-order intake); on
  // Path A the backend resolves it server-side from the ordering
  // clinician's own portal account, same reasoning as practiceName/
  // providerName/providerNpi being disabled+server-sourced on Path A.
  const phoneTouchedInvalid = sendToClinician && providerPhone.trim() &&
    providerPhone.replace(/\D/g, "").length < 10;
  const zipInvalid = address.zip.trim() && !isValidZip(address.zip);
  const identityIncomplete = !usingOrder && (
    !firstName.trim() || !lastName.trim() || !dob.trim() ||
    !address.line1.trim() || !address.city.trim() || !address.state.trim() || !address.zip.trim()
  );
  const providerIncomplete = sendToClinician && !usingOrder && (
    !practiceName.trim() || !providerName.trim() || !providerNpi.trim() || npiTouchedInvalid ||
    !providerPhone.trim() || phoneTouchedInvalid
  );
  const manualExtrasIncomplete = !usingOrder && !manualSpecimenId.trim();
  const filesIncomplete = (sendToPatient && !patientFile) || (sendToClinician && !clinicianFile);
  // 2026-10-22 (Piece 5, one-identity-one-email invariant) — widened from
  // "required only when the Patient toggle is on" to always required,
  // matching the backend's own unconditional 422 (see upload_proove_
  // report's own comment on this reversal).
  const patientEmailMissing = !patientEmail.trim();

  const canSubmit = (
    !!prooveTestId && !filesIncomplete && !submitting && !zipInvalid && !patientEmailMissing &&
    !(usingOrder ? false : identityIncomplete) &&
    !(usingOrder ? false : providerIncomplete) &&
    !(usingOrder ? false : manualExtrasIncomplete)
  );

  const buildIdentityFields = (fd) => {
    if (usingOrder) {
      fd.append("order_id", orderContext.order_id);
      return;
    }
    fd.append("patient_first_name", firstName.trim());
    fd.append("patient_last_name", lastName.trim());
    fd.append("patient_dob", dob.trim());
    fd.append("patient_address_line1", address.line1.trim());
    fd.append("patient_address_unit", (address.unit || "").trim());
    fd.append("patient_address_city", address.city.trim());
    fd.append("patient_address_state", address.state.trim());
    fd.append("patient_address_zip", address.zip.trim());
    fd.append("practice_name", practiceName.trim());
    fd.append("provider_npi", providerNpi.trim());
    fd.append("provider_name", providerName.trim());
    fd.append("provider_phone", providerPhone.trim());
    fd.append("manual_specimen_id", manualSpecimenId.trim());
    fd.append("collection_date", collectionDate.trim());
  };

  // 2026-10-16 fix (security-auditor finding, post-swarm) — a partial
  // failure (one flavor archived, the other didn't) used to leave staff
  // with only "Close" and an advisory to "contact engineering before
  // retrying" — nothing stopped them from just reopening this dialog and
  // clicking upload again, which for a Path-B (manual/no order_id)
  // upload has no server-side dedup and WOULD create a second summary row
  // + orphan GCS object for the flavor that already succeeded. Fixed by
  // retrying ONLY the failed flavor through the existing, unchanged,
  // already-reviewed single-PDF endpoint — the succeeded flavor's file
  // is never resubmitted at all.
  const retryFailedFlavor = async () => {
    if (!partialResult) return;
    const failedKind = partialResult.patient?.error ? "patient" : "clinician";
    const failedFile = failedKind === "patient" ? patientFile : clinicianFile;
    const failedJsonFile = failedKind === "patient" ? patientJsonFile : clinicianJsonFile;
    if (!failedFile) {
      setError("The failed report's file is no longer available in this dialog — please close and start over.");
      return;
    }
    setSubmitting(true); setError("");
    try {
      const fd = new FormData();
      fd.append("pdf", failedFile);
      fd.append("report_kind", failedKind);
      fd.append("proove_test_id", prooveTestId);
      fd.append("patient_email", patientEmail.trim());
      buildIdentityFields(fd);
      if (PROOVE_REPORT_JSON_ENABLED && failedJsonFile) fd.append("json_file", failedJsonFile);
      await api.post("/api/proove-reports/upload", fd);
      // Both flavors are now archived (the one that succeeded the first
      // time, plus this retry) — safe to show the full success state.
      setPartialResult(null);
      setSuccess(true);
      setSuccessLabel("Both the patient and clinician reports were uploaded");
      if (onUploaded) onUploaded();
    } catch (err) {
      setError(extractErrorDetail(err, `Could not upload the ${failedKind} report — please try again or contact engineering.`));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    setTouched(true);
    if (!canSubmit) return;
    setSubmitting(true); setError(""); setPartialResult(null);
    try {
      if (bothOn) {
        const fd = new FormData();
        fd.append("pdf_patient", patientFile);
        fd.append("pdf_clinician", clinicianFile);
        fd.append("proove_test_id", prooveTestId);
        fd.append("patient_email", patientEmail.trim());
        buildIdentityFields(fd);
        if (PROOVE_REPORT_JSON_ENABLED && patientJsonFile) fd.append("json_patient", patientJsonFile);
        if (PROOVE_REPORT_JSON_ENABLED && clinicianJsonFile) fd.append("json_clinician", clinicianJsonFile);
        const resp = await api.post("/api/proove-reports/upload-combined", fd);
        if (resp.data?.partial) {
          // A real partial outcome — one flavor archived, the other
          // failed after it. Never shown as a plain "Report uploaded.";
          // the succeeded flavor's result is never discarded either.
          setPartialResult(resp.data);
        } else {
          setSuccess(true);
          setSuccessLabel("Both the patient and clinician reports were uploaded");
        }
      } else {
        // 2026-10-21 revamp — exactly one recipient toggled on. Reuses
        // the SAME single-PDF endpoint retryFailedFlavor already called
        // for the partial-failure recovery case, just reachable directly
        // now instead of only as a retry path.
        const kind = sendToPatient ? "patient" : "clinician";
        const file = sendToPatient ? patientFile : clinicianFile;
        const jsonFile = sendToPatient ? patientJsonFile : clinicianJsonFile;
        const fd = new FormData();
        fd.append("pdf", file);
        fd.append("report_kind", kind);
        fd.append("proove_test_id", prooveTestId);
        fd.append("patient_email", patientEmail.trim());
        buildIdentityFields(fd);
        if (PROOVE_REPORT_JSON_ENABLED && jsonFile) fd.append("json_file", jsonFile);
        await api.post("/api/proove-reports/upload", fd);
        setSuccess(true);
        setSuccessLabel(`The ${kind} report was uploaded`);
      }
      if (onUploaded) onUploaded();
    } catch (err) {
      setError(extractErrorDetail(err, "Could not upload this report. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const prooveTestOptions = usingOrder && orderContext.proove_test_ids?.length
    ? PROOVE_TEST_OPTIONS.filter((o) => orderContext.proove_test_ids.includes(o.value))
    : PROOVE_TEST_OPTIONS;

  const submitLabel = bothOn ? "Send to Both" : sendToPatient ? "Send to Patient" : "Send to Clinician";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontWeight: 700, color: BRAND_NAVY }}>Upload Proove Report</Typography>
          {!success && !partialResult && (
            <Typography sx={{ fontSize: 12.5, color: "#94a3b8", mt: 0.25 }}>
              {orderContext ? `Order ${orderContext.order_id}` : "Manual upload — no matching order"}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#fafbfc" }}>
        {success ? (
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            {successLabel}{usingOrder ? " — this order is no longer awaiting a Proove report." : "."}
          </Alert>
        ) : partialResult ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              The {partialResult.patient?.identity_id ? "patient" : "clinician"} report was saved and archived
              successfully. The {partialResult.patient?.identity_id ? "clinician" : "patient"} report
              FAILED to archive and was NOT saved.
              {usingOrder ? " This order will stay in the Awaiting Proove Report queue until both reports upload successfully." : ""}
              {" "}Click "Retry Failed Report" below to resubmit only the failed one — do not reopen this
              dialog and upload both again, which could save the already-succeeded report a second time.
            </Alert>
            {error && <Alert severity="warning" sx={{ borderRadius: 2 }} onClose={() => setError("")}>{error}</Alert>}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl size="small" sx={{ maxWidth: 420 }} error={touched && !prooveTestId}>
              <InputLabel>Proove Test *</InputLabel>
              <Select label="Proove Test *" value={prooveTestId} onChange={(e) => setProoveTestId(e.target.value)}>
                {prooveTestOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
              {touched && !prooveTestId && (
                <Typography sx={{ fontSize: 11.5, color: "#dc2626", mt: 0.5, ml: 1.5 }}>Choose which Proove test this report is for.</Typography>
              )}
            </FormControl>

            <SectionHeader required>Send To</SectionHeader>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <RecipientCard
                active={sendToPatient} onToggle={() => toggleRecipient("patient")}
                color={BRAND_NAVY} colorDark={BRAND_NAVY_DARK}
                icon={<PersonOutlineIcon sx={{ fontSize: 20 }} />}
                title="Patient Copy" subtitle="Delivered to the patient's own account"
                file={patientFile} onFile={setPatientFile}
                fileRequired={sendToPatient} fileError={touched}
                jsonEnabled={PROOVE_REPORT_JSON_ENABLED} jsonFile={patientJsonFile} onJsonFile={setPatientJsonFile}
              />
              <RecipientCard
                active={sendToClinician} onToggle={() => toggleRecipient("clinician")}
                color={BRAND_GREEN} colorDark={BRAND_GREEN_DARK}
                icon={<LocalHospitalOutlinedIcon sx={{ fontSize: 20 }} />}
                title="Clinician Copy" subtitle="Filed under the ordering provider"
                file={clinicianFile} onFile={setClinicianFile}
                fileRequired={sendToClinician} fileError={touched}
                jsonEnabled={PROOVE_REPORT_JSON_ENABLED} jsonFile={clinicianJsonFile} onJsonFile={setClinicianJsonFile}
              />
            </Box>
            {!bothOn && (
              <Chip
                size="small" icon={<PictureAsPdfIcon sx={{ fontSize: 14 }} />}
                label={`Sending ${sendToPatient ? "only the patient copy" : "only the clinician copy"} — the other recipient won't receive this report.`}
                sx={{ alignSelf: "flex-start", bgcolor: "#fff7ed", color: "#9a3412", fontSize: 12, height: 26 }}
              />
            )}

            <SectionHeader required>Patient</SectionHeader>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="First Name *" size="small" value={firstName} disabled={usingOrder}
                onChange={(e) => setFirstName(e.target.value)}
                error={touched && !usingOrder && !firstName.trim()}
                helperText={touched && !usingOrder && !firstName.trim() ? "Required" : " "}
                sx={{ flex: 1, minWidth: 150, ...readOnlyFieldSx }}
              />
              <TextField
                label="Last Name *" size="small" value={lastName} disabled={usingOrder}
                onChange={(e) => setLastName(e.target.value)}
                error={touched && !usingOrder && !lastName.trim()}
                helperText={touched && !usingOrder && !lastName.trim() ? "Required" : " "}
                sx={{ flex: 1, minWidth: 150, ...readOnlyFieldSx }}
              />
              <TextField
                label="Date of Birth *" type="date" size="small" value={dob} disabled={usingOrder}
                onChange={(e) => setDob(e.target.value)} InputLabelProps={{ shrink: true }}
                error={touched && !usingOrder && !dob.trim()}
                helperText={touched && !usingOrder && !dob.trim() ? "Required" : " "}
                sx={{ width: 170, ...readOnlyFieldSx }}
              />
            </Box>
            <Box sx={{ maxWidth: 480, mt: -1 }}>
              {usingOrder ? (
                // AddressFields has no read-only/disabled mode of its own
                // (it's always an editable Autocomplete/TextField group) —
                // rendering plain text here instead of passing an
                // unsupported prop is what actually makes this read-only,
                // not just visually greyed, per the "staff cannot retype
                // it" requirement.
                <Typography sx={{ fontSize: 13.5, color: "#666", bgcolor: "#F4F4F5", borderRadius: 1.5, px: 1.5, py: 1 }}>
                  {address.line1} {address.unit} &middot; {address.city}, {address.state} {address.zip}
                </Typography>
              ) : (
                <AddressFields isDark={false} value={address} onChange={setAddress} showErrors={touched} />
              )}
            </Box>

            {!usingOrder && (
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Specimen / Accession # *"
                  size="small"
                  value={manualSpecimenId}
                  onChange={(e) => setManualSpecimenId(e.target.value)}
                  error={touched && !manualSpecimenId.trim()}
                  helperText={touched && !manualSpecimenId.trim() ? "Required for a manual upload" : "Used to identify and de-duplicate this report."}
                  sx={{ maxWidth: 260 }}
                />
                <TextField
                  label="Collection Date"
                  type="date"
                  size="small"
                  value={collectionDate}
                  onChange={(e) => setCollectionDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Optional — off the paper requisition, if available."
                  sx={{ width: 200 }}
                />
              </Box>
            )}

            {/* 2026-10-22 (Piece 5) — always rendered now, regardless of
                which Send-To toggle is on (previously wrapped in
                {sendToPatient && (...)}, so a clinician-only send never
                showed or required this field at all). Explicitly labeled
                "Patient," not a bare "Email," so staff sending only the
                clinician copy aren't confused about whose email this is —
                it's still the patient's, used here to verify identity. */}
            <TextField
              label="Patient Email *"
              type="email"
              size="small"
              value={patientEmail}
              onChange={(e) => setPatientEmail(e.target.value)}
              error={touched && patientEmailMissing}
              helperText={
                touched && patientEmailMissing
                  ? "Required — used to notify the patient (if sending to them) and to verify this patient's identity on file."
                  : "Verifies this patient's identity on file, and notifies them their report is ready if you're sending them a copy."
              }
              sx={{ maxWidth: 320 }}
            />

            {sendToClinician && (
              <>
                <SectionHeader required>Ordering Provider</SectionHeader>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    label="Practice Name *" size="small" value={practiceName} disabled={usingOrder}
                    onChange={(e) => setPracticeName(e.target.value)}
                    error={touched && !usingOrder && !practiceName.trim()}
                    helperText={touched && !usingOrder && !practiceName.trim() ? "Required" : " "}
                    sx={{ flex: 1, minWidth: 170, ...readOnlyFieldSx }}
                  />
                  <TextField
                    label="Provider Name *" size="small" value={providerName} disabled={usingOrder}
                    onChange={(e) => setProviderName(e.target.value)}
                    error={touched && !usingOrder && !providerName.trim()}
                    helperText={touched && !usingOrder && !providerName.trim() ? "Required" : " "}
                    sx={{ flex: 1, minWidth: 170, ...readOnlyFieldSx }}
                  />
                  <Tooltip title="Must be the real 10-digit National Provider Identifier" placement="top" arrow>
                    <TextField
                      label="Provider NPI *" size="small" value={providerNpi} disabled={usingOrder}
                      onChange={(e) => setProviderNpi(e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
                      error={!usingOrder && ((touched && !providerNpi.trim()) || npiTouchedInvalid)}
                      helperText={
                        !usingOrder && npiTouchedInvalid ? "Must be exactly 10 digits"
                          : !usingOrder && touched && !providerNpi.trim() ? "Required" : " "
                      }
                      sx={{ width: 170, ...readOnlyFieldSx }}
                    />
                  </Tooltip>
                  {!usingOrder && (
                    <TextField
                      label="Provider Phone *" size="small" value={providerPhone}
                      onChange={(e) => setProviderPhone(e.target.value)}
                      error={touched && (!providerPhone.trim() || phoneTouchedInvalid)}
                      helperText={
                        phoneTouchedInvalid ? "Must be a valid 10-digit phone number"
                          : touched && !providerPhone.trim() ? "Required" : " "
                      }
                      sx={{ flex: 1, minWidth: 170 }}
                    />
                  )}
                </Box>
                {usingOrder && (
                  <Box sx={{ mt: -1, mb: 1 }}>
                    <span style={{ fontSize: 12.5, color: "rgba(0,0,0,0.6)" }}>
                      Provider phone is taken from this clinician's own account — no entry needed here.
                    </span>
                  </Box>
                )}
              </>
            )}

            {orderContext && (
              <Button size="small" onClick={() => setManualMode((m) => !m)} sx={{ textTransform: "none", alignSelf: "flex-start", fontSize: 12.5 }}>
                {manualMode ? "Use this order's details instead" : "Can't find this order? Enter details manually"}
              </Button>
            )}

            {error && <Alert severity="warning" sx={{ borderRadius: 2 }} onClose={() => setError("")}>{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>{(success || partialResult) ? "Close" : "Cancel"}</Button>
        {partialResult && (
          <Button variant="contained" disabled={submitting} onClick={retryFailedFlavor}
            sx={{ bgcolor: BRAND_NAVY, "&:hover": { bgcolor: "#002a5c" }, textTransform: "none" }}>
            {submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Retry Failed Report"}
          </Button>
        )}
        {!success && !partialResult && (
          <Button variant="contained" disabled={submitting} onClick={submit}
            sx={{ bgcolor: BRAND_NAVY, "&:hover": { bgcolor: "#002a5c" }, textTransform: "none" }}>
            {submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : submitLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
